import { P } from 'components'
import PMBSelect from 'components/PMBSelect'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLazyFetchTransactionPicQuery } from '../dashboardApiSlice'

const DropdownTransactionPic = ({
  title = 'PIC',
  placeholderDesc = 'Semua',
  value,
  onChange,
  transactionFilters,
  profile,
  testId = 'transaction-filter-pic-select',
}) => {
  const [options, setOptions] = useState([])
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const [search, setSearch] = useState('')
  const [isFetching, setIsFetching] = useState(false)

  const searchTimeoutRef = useRef(null)
  const [fetchTransactionPic] = useLazyFetchTransactionPicQuery()

  const loadOptions = useCallback(
    async (searchQuery, pageNum, append = false) => {
      setIsFetching(true)
      try {
        const response = await fetchTransactionPic({
          ...transactionFilters,
          searchPicName: searchQuery || undefined,
          page: pageNum,
          size: 20,
          sortby: 'name',
          sortdir: 'asc',
        }).unwrap()

        const items = response?.items || []
        const mapped = items
          .map((item) => {
            const userId =
              item?.picSellerUserId ??
              item?.picBuyerUserId ??
              item?.userId ??
              item?.id
            const userName =
              item?.picSellerUserName ??
              item?.picBuyerUserName ??
              item?.userName ??
              item?.name ??
              '-'

            if (userId === undefined || userId === null) return null

            return {
              value: userId,
              userId,
              relationUserId: userId,
              label: userName,
              fullName: userName,
            }
          })
          .filter(Boolean)

        if (append) {
          setOptions((prev) => {
            const existingIds = new Set(prev.map((opt) => String(opt.value)))
            const newUnique = mapped.filter(
              (opt) => !existingIds.has(String(opt.value)),
            )
            return [...prev, ...newUnique]
          })
        } else {
          setOptions(mapped)
        }

        const totalPages = response?.totalPages ?? 1
        setHasMore(pageNum < totalPages - 1)
      } catch (err) {
        console.error('Error fetching transaction PIC list:', err)
      } finally {
        setIsFetching(false)
      }
    },
    [fetchTransactionPic, transactionFilters],
  )

  // Reload options when transaction filters change
  useEffect(() => {
    setSearch('')
    setPage(0)
    setHasMore(true)
    loadOptions('', 0, false)
  }, [loadOptions])

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    }
  }, [])

  const handleInputChange = (val, { action }) => {
    if (action === 'input-change') {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
      searchTimeoutRef.current = setTimeout(() => {
        setSearch(val)
        setPage(0)
        loadOptions(val, 0, false)
      }, 400)
    }
  }

  const handleMenuScrollToBottom = () => {
    if (!isFetching && hasMore) {
      const nextPage = page + 1
      setPage(nextPage)
      loadOptions(search, nextPage, true)
    }
  }

  const selectedValue = useMemo(() => {
    if (value === undefined || value === null || value === '') return null
    if (typeof value === 'object') return value
    const found = options.find((opt) => String(opt.value) === String(value))
    if (found) return found
    if (profile?.userId && String(profile.userId) === String(value)) {
      return { value: profile.userId, label: profile.fullName || 'Hanya Saya' }
    }
    return { value, label: `User ${value}` }
  }, [value, options, profile])

  return (
    <div style={{ position: 'relative' }} className="w-100">
      <P size="default" className="mb-2">
        {title}
      </P>
      <PMBSelect
        style={{ width: '100%' }}
        options={options}
        value={selectedValue}
        onChange={(val) => {
          if (onChange) {
            onChange(val)
          }
        }}
        placeholder={placeholderDesc}
        isLoading={isFetching}
        isClearable
        onInputChange={handleInputChange}
        onMenuScrollToBottom={handleMenuScrollToBottom}
        filterOption={() => true}
        testId={testId}
      />
    </div>
  )
}

export default DropdownTransactionPic
